import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { SOTProductViewModel } from '../../models';
import { BaseUnit } from '../../../core/shared/models';
import Utils from '../../../core/shared/utils';
import { SOTService } from '../../services';
import * as Models from '../../models';

@Component({
  selector: 'sot-product-detail-view',
  templateUrl: "./productDetailView.component.html",
  styleUrls: ["./productDetailView.component.scss"],
})
export class ProductDetailViewComponent implements OnInit {

  @Input() product: SOTProductViewModel;
  @Input() requiredPoints: number;
  @Input() canAddProductToBasket: boolean = false;
  @Input() benefitImageUrl?: string;
  @Input() benefitHasImage?: boolean;

  /** Image path — benefit image if set, else product image */
  public get displayImagePath(): string {
    const me = this;
    return me.benefitImageUrl ? me.benefitImageUrl : (me.product ? me.product.image_path : "");
  }

  /** Whether to display an image — benefit-aware */
  public get displayHasImage(): boolean {
    const me = this;
    if (me.benefitImageUrl) { return !!me.benefitHasImage; }
    return !!me.product && me.product.has_image;
  }

  @Output() onBack = new EventEmitter<void>();
  @Output() onAddToBasket = new EventEmitter<Models.SOTBasketItemExtraGroupViewModel[]>();

  private readonly MAX_TAG_DISPLAY_COUNT = 5;
  private readonly BENEFIT_EXTRA_PRICE = 0;
  public readonly deposit_langkey = "details_plus_deposit";
  public readonly baseUnit = BaseUnit;

  public tempBasket: Models.SOTBasketViewModel;
  public extraGroups: Models.SOTProductExtraGroupViewModel[] = [];

  public get hasExtras(): boolean {
    const me = this;
    return me.extraGroups.some(eg => !eg.hasNoChoice);
  }

  public selectedProduct: Models.SOTProductViewModel;
  public extraProductPrice: number = 0;

  public itemTagsViewModel: Models.SOTProductTagViewModel[];
  public isItemTagEnabled: boolean = false;

  public showProductAdditionalInfo: boolean = false;
  public showExtraAdditionalInfo: boolean = false;
  private _hasAttemptedAdd = false;

  public get highlight_required(): boolean {
    const me = this;
    return me._hasAttemptedAdd && !me.areExtraGroupRequirementsMet();
  }

  constructor(
    private _sotService: SOTService,
    private _changeDetectorRef: ChangeDetectorRef
  ) { }

  public ngOnInit(): void {
    const me = this;
    me.tempBasket = me.product ? me._sotService.GetNewProductBasket(me.product) : me._sotService.GetBasket();
    if (me.product) {
      me.extraGroups = me.product.extraGroups || [];
      me._getItemTagSettings();
      me.pushChanges();
    }
  }


  private pushChanges(): void {
    const me = this;
    try {
      me._changeDetectorRef.detectChanges();
    } catch (e) {
      me._sotService.coreLogService.error("ProductDetailViewComponent.pushChanges", "_changeDetectorRef.detectChanges (catch)", e);
    }
  }

  /** Accessibility mode two is active */
  public get isModeTwo(): boolean {
    const me = this;
    return me._sotService.Accessabilty.useModeTwo;
  }

  /** Accessibility mode one is active */
  public get isModeOne(): boolean {
    const me = this;
    return me._sotService.Accessabilty.useModeOne;
  }

  /** eCommerce is enabled */
  public get use_eCommerce(): boolean {
    const me = this;
    return me._sotService.GetSettings().use_eCommerce;
  }

  /** Extra items are displayed as images */
  public get extra_as_image() {
    const me = this;
    return me._sotService.GetSettings().extra_as_image;
  }

  //#region Navigation Actions

  public backToListBtn() {
    const me = this;
    me.onBack.emit();
  }

  /**
   * Checks whether the product can be added to the basket.
   */
  public checkRequirementAndAddToBasket() {
    const me = this;
    if (!me.hasExtras) {
      me.canAddProductToBasket ? me.onAddToBasket.emit(me.tempBasket.products[0].extra_groups || []) : null;
    } else {
      if (me.canAddProductToBasket && me.areExtraGroupRequirementsMet()) {
        me.onAddToBasket.emit(me.tempBasket.products[0].extra_groups || []);
      } else {
        me._hasAttemptedAdd = true;
        me.pushChanges();
      }
    }
  }

  /**
   *  Checks if the requirements of the extra groups are met
   */
  public areExtraGroupRequirementsMet(): boolean {
    const me = this;
    return me.extraGroups.every(eg => !eg.force_mode || eg.max_choices === me.getCount(eg));
  }

  //#endregion

  //#region Product Display

  /**
   * Returns the price shown to the user as base price.
   * Prefilled NoChoice extras are folded into it because their individual prices are hidden in the UI.
   */
  public getBasketProductShownBasePrice(basketProduct: Models.SOTBasketProductViewModel) {
    const sumOfPrefilledExtras = basketProduct.extra_groups.filter(eg => eg.model.hasNoChoice).reduce((a, b) => a + b.priceSum, 0);
    return basketProduct.basePrice + sumOfPrefilledExtras;
  }

  /**
   * Whether the per unit base price  should be shown.
   */
  public showBasePrice(product: SOTProductViewModel): boolean {
    if (product.details.item_Base_Unit === BaseUnit.NONE) { return false; }
    if (product.details.item_Base_Factor === 1) { return false; }
    if (product.price <= 0) { return false; }
    return true;
  }

  /** Extra item  base unit */
  public extraBaseUnit(product: SOTProductViewModel): BaseUnit {
    return product.details.item_Base_Unit;
  }

  /** Extra item price per unit */
  public extraPricePerUnit(product: SOTProductViewModel): number {
    return Utils.pricePerUnit(product.priceWithExtras, product.details.item_Base_Factor);
  }

  //#endregion

  //#region Additional Information

  /** Loads the product with extras layout */
  public get loadProductWithExtrasLayout() {
    const me = this;
    return !me.showProductAdditionalInfo && !me.showExtraAdditionalInfo;
  }

  /** Toggles the display of additional product information */
  public toggleProductAdditionalInfo(show: boolean): void {
    const me = this;
    me.showProductAdditionalInfo = show;
    me.pushChanges();
  }

  /** Toggles the display of extra additional product information */
  public toggleExtraAdditionalInfo(show: boolean) {
    const me = this;
    me.showExtraAdditionalInfo = show;
    me.pushChanges();
  }

  /** Displays the additional information of the product or extra product */
  public displayAdditionalInfo(product: Models.SOTProductViewModel, extraProductPrice?: number, event?: Event): void {
    const me = this;
    if (!me.showProductAdditionalInfo && me.showExtraAdditionalInfo) {
      me.extraProductPrice = extraProductPrice;
      if (event) { event.stopPropagation(); }
    }
    me.selectedProduct = product;
    me.pushChanges();
  }

  /** Checks whether additional information can be displayed for the extra product */
  public canDisplayAdditionalInformation(extraProduct: Models.SOTProductViewModel): boolean {
    const me = this;
    const settings = me._sotService.GetSettings();
    return settings && extraProduct && extraProduct.details &&
      (me.hasCO2Information(settings, extraProduct) ||
        me.hasAdditivesInformation(settings, extraProduct) ||
        me.hasNutritionInformation(settings, extraProduct) ||
        me.hasAllergeneInformation(settings, extraProduct) ||
        me.hasECommerceInformation(settings, extraProduct));
  }

  //#endregion

  //#region Extra Groups

  /** Handles the click event on an extra item */
  public onItemClick(event: Event, extragroup: Models.SOTProductExtraGroupViewModel, extra: Models.SOTProductExtraItemViewModel, sub?: Models.SOTProductExtraItemViewModel) {
    event.stopPropagation();
    if (extragroup.max_choices === 1) {
      this.toggleExtraInSingleChoice(extragroup, extra, sub);
    } else {
      this.addExtra(extragroup, extra, sub);
    }
  }

  /** Gets the count of extras in a group */
  public getCount(extragroup: Models.SOTProductExtraGroupViewModel, extra?: Models.SOTProductExtraItemViewModel, sub?: Models.SOTProductExtraItemViewModel) {
    const existingExtraGroup = this._findBasketExtraGroup(extragroup);
    if (extra) {
      const existingExtra = existingExtraGroup ? existingExtraGroup.extras.find(e => e.extra_id === extra.id) : null;
      if (sub) {
        const existingSub = existingExtra ? existingExtra.sub_extras.find(e => e.extra_id === sub.id) : null;
        return existingSub ? existingSub.count : 0;
      }
      return existingExtra ? existingExtra.count : 0;
    }
    return existingExtraGroup ? existingExtraGroup.extras.reduce((a, b) => a + b.count, 0) : 0;
  }

  /** Checks whether an extra item is selected */
  public isExtraSelected(extragroup: Models.SOTProductExtraGroupViewModel, extra: Models.SOTProductExtraItemViewModel, sub?: Models.SOTProductExtraItemViewModel) {
    return this.getCount(extragroup, extra, sub) > 0;
  }

  /** Sets the count of an extra item */
  public setExtraCountTo(count: number, extragroup: Models.SOTProductExtraGroupViewModel, extra: Models.SOTProductExtraItemViewModel, sub?: Models.SOTProductExtraItemViewModel) {
    while (this.getCount(extragroup, extra, sub) > count) {
      this.removeExtra(extragroup, extra, sub);
    }
    while (this.getCount(extragroup, extra, sub) < count && this.canAddExtra(extragroup, extra, sub)) {
      this.addExtra(extragroup, extra, sub);
    }
    this.pushChanges();
  }

  /** Checks whether an extra item can be added */
  public canAddExtra(extragroup: Models.SOTProductExtraGroupViewModel, extra: Models.SOTProductExtraItemViewModel, sub?: Models.SOTProductExtraItemViewModel) {
    return this.getCount(extragroup) < extragroup.max_choices && this.isExtraAvailable(extra, sub);
  }

  /** Adds an extra item */
  public addExtra(extragroup: Models.SOTProductExtraGroupViewModel, extra: Models.SOTProductExtraItemViewModel, sub?: Models.SOTProductExtraItemViewModel) {
    if (this.canAddExtra(extragroup, extra, sub)) {
      this.tempBasket.products[0].addExtraGroup(extragroup.query, extra.id, sub ? sub.id : 0);
    }
  }

  /** Checks whether an extra item can be removed */
  public canRemoveExtra(extragroup: Models.SOTProductExtraGroupViewModel, extra: Models.SOTProductExtraItemViewModel, sub?: Models.SOTProductExtraItemViewModel) {
    return this.getCount(extragroup, extra, sub) > 0;
  }

  /** Removes an extra item */
  public removeExtra(extragroup: Models.SOTProductExtraGroupViewModel, extra: Models.SOTProductExtraItemViewModel, sub?: Models.SOTProductExtraItemViewModel) {
    if (this.canRemoveExtra(extragroup, extra, sub)) {
      this.tempBasket.products[0].removeExtraGroup(extragroup.query, extra.id, sub ? sub.id : 0);
    }
  }

  /** Checks whether an extra item is available */
  public isExtraAvailable(extra: Models.SOTProductExtraItemViewModel, sub?: Models.SOTProductExtraItemViewModel): boolean {
    return extra.product && extra.product.is_available && (sub ? sub.product && sub.product.is_available : true);
  }

  /** Displays the price of an extra item */
  public displayExtraPrice(price: number) {
    const me = this;
    return me.requiredPoints ? me.BENEFIT_EXTRA_PRICE : price;
  }

  //#endregion

  //#region Tag Filter

  /** Gets the tags of extra products by their IDs */
  public getExtraProductTagsByIds(tagIds: number[]) {
    const me = this;
    const tags = me._sotService.GetProductTagsByIds(tagIds);
    if (me._sotService.hasTagSelected) {
      return tags.sort((a, b) => me.displaySelectedTagFirst(a.id, b.id)).slice(0, me.MAX_TAG_DISPLAY_COUNT);
    }
    return tags.slice(0, me.MAX_TAG_DISPLAY_COUNT);
  }

  /** Displays the selected tag first */
  public displaySelectedTagFirst(a_tagId: number, b_tagId: number) {
    const me = this;
    return me._sotService.displaySelectedTagFirst(a_tagId, b_tagId);
  }

  /** Fades out an extra item based on the selected tag filter */
  public fadeOutExtraBySelectedTag(extra: Models.SOTProductExtraItemViewModel): boolean {
    const me = this;
    if (!me._sotService.hasTagSelected) { return false; }
    const selectedTagFilter = me._sotService.GetProductTagFilter();
    const hasSomeProductTags = (product_tag_ids: number[]): boolean => {
      return selectedTagFilter.every(id => product_tag_ids.indexOf(id) >= 0);
    };
    if (extra.product.product_tag_ids.length > 0 && !me.isResetTagFilterActive(extra.id)) {
      return !hasSomeProductTags(extra.product.product_tag_ids);
    }
    return false;
  }

  /** Opens the reset extra tag filter */
  public openResetExtraTagFilter(extra: Models.SOTProductExtraItemViewModel) {
    const me = this;
    if (me.isExtraAvailable(extra)) {
      me._sotService.openResetExtraTagFilter().then((result) => {
        if (result) {
          me._sotService.setResetTagFilterExtraIds(extra.id);
          me.pushChanges();
        }
      });
    }
  }

  /** Checks whether the reset tag filter is active for an extra item */
  public isResetTagFilterActive(extraId: number): boolean {
    const me = this;
    return me._sotService.isResetTagFilterActive(extraId);
  }

  //#endregion

  //#region Helper Methods

  /** Finds the basket extra group for a given extra group */
  private _findBasketExtraGroup(extragroup: Models.SOTProductExtraGroupViewModel): Models.SOTBasketItemExtraGroupViewModel {
    const me = this;
    if (!me.tempBasket || !me.tempBasket.products || !me.tempBasket.products[0] || !me.tempBasket.products[0].extra_groups) {
      return null;
    }
    return me.tempBasket.products[0].extra_groups.find(eg => eg.query === extragroup.query);
  }

  /** Toggles an extra item in a single choice group */
  private toggleExtraInSingleChoice(extragroup: Models.SOTProductExtraGroupViewModel, extra: Models.SOTProductExtraItemViewModel, sub?: Models.SOTProductExtraItemViewModel) {
    const me = this;
    if (me.isExtraAvailable(extra, sub)) {
      if (me.getCount(extragroup, extra, sub) === 0) {
        me._removeAllExtras(extragroup);
        me.addExtra(extragroup, extra, sub);
      } else {
        // in a single choice group this will always be the only selected extra
        me.removeExtra(extragroup, extra, sub);
      }
    }
    me.pushChanges();
  }

  /** Removes all extras of an extra group */
  private _removeAllExtras(extragroup: Models.SOTProductExtraGroupViewModel) {
    const me = this;
    const basketExtraGroup = me._findBasketExtraGroup(extragroup);
    if (basketExtraGroup) {
      basketExtraGroup.extras.forEach((basketExtra) => {
        basketExtra.sub_extras.forEach((basketSubExtra) => {
          me.tempBasket.products[0].removeExtraGroup(extragroup.query, basketExtra.extra_id, basketSubExtra.extra_id);
        });
        me.tempBasket.products[0].removeExtraGroup(extragroup.query, basketExtra.extra_id);
      });
    }
  }

  /** Gets the item tag settings */
  private async _getItemTagSettings() {
    const me = this;
    const webshopSettings = await me._sotService.getWebshopSettingsDataModel();
    if (webshopSettings) {
      me.isItemTagEnabled = webshopSettings.is_itemtag_sot_enabled;
      if (me.isItemTagEnabled && me.product && me.product.product_tag_ids) {
        me.itemTagsViewModel = me._sotService.GetProductTagsByIds(me.product.product_tag_ids);
      }
      me.pushChanges();
    }
  }

  /** Checks whether the CO2 information is available for an extra product */
  private hasCO2Information(settings: Models.SOTSettingsViewModel, extraProduct: Models.SOTProductViewModel) {
    return settings.use_co2 && extraProduct.details.co2 > 0;
  }

  /** Checks whether the additives information is available for an extra product */
  private hasAdditivesInformation(settings: Models.SOTSettingsViewModel, extraProduct: Models.SOTProductViewModel) {
    return settings.use_additives && Utils.ValidateArray(extraProduct.details.additives);
  }

  /** Checks whether the nutrition information is available for an extra product */
  private hasNutritionInformation(settings: Models.SOTSettingsViewModel, extraProduct: Models.SOTProductViewModel) {
    return settings.use_nutrition_values && extraProduct.details.nutritions && Utils.ValidateArray(extraProduct.details.nutritions.nutritionItemViewModels);
  }

  /** Checks whether the allergene information is available for an extra product */
  private hasAllergeneInformation(settings: Models.SOTSettingsViewModel, extraProduct: Models.SOTProductViewModel) {
    return settings.use_allergens && Utils.ValidateArray(extraProduct.details.allergens);
  }

  /** Checks whether the eCommerce information is available for an extra product */
  private hasECommerceInformation(settings: Models.SOTSettingsViewModel, extraProduct: Models.SOTProductViewModel) {
    return settings.use_eCommerce && (!Utils.IsNullOrWhiteSpace(extraProduct.details.item_Content) || extraProduct.details.hasProvider_Info);
  }

  //#endregion

}
